import React from 'react';
import { motion } from 'framer-motion';
import { ArrowTrendingUpIcon, CloudIcon, BeakerIcon, GlobeAsiaAustraliaIcon, SunIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const PrecisionFarming = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const deliverables = [
        "Greenhouse & Shade Net Solutions",
        "Climate-Controlled Crop Production",
        "Precision Nutrient & Irrigation Management",
        "Real-Time Crop Monitoring",
        "Optimized Resource Utilization"
    ];

    const benefits = [
        { title: "Higher yield per square meter", icon: ArrowTrendingUpIcon },
        { title: "Reduced water & input wastage", icon: BeakerIcon },
        { title: "Improved crop quality & uniformity", icon: GlobeAsiaAustraliaIcon },
        { title: "Protection from extreme weather", icon: CloudIcon },
        { title: "Year-round cultivation capability", icon: SunIcon }
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-black/60 z-10"></div>
                    <img
                        src={require('../../../assets/newimages/mas wesbite protected farming.webp')}
                        alt="Precision Farming"
                        className="w-full h-full object-cover opacity-80"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-5xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
                    >
                        Protected & <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">Precision Farming</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-xl md:text-2xl text-gray-200 font-light"
                    >
                        Controlled Environment. Calculated Performance.
                    </motion.p>
                </div>
            </section>

            <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-1/2"
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            <strong className="text-gray-900 font-bold">Protected & Precision Farming</strong> at Marvelous Agri Solution combines climate control with data intelligence to deliver higher yield, superior quality, and predictable results.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8 font-light">
                            We integrate modern greenhouse systems, shade nets, and smart farming technologies to eliminate uncertainty and maximize productivity.
                        </p>

                        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-2xl mb-12">
                            <p className="text-xl font-medium text-emerald-900 italic">
                                This is farming designed for consistency and performance.
                            </p>
                        </div>

                        <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                            What We Deliver
                        </h3>

                        <div className="space-y-4 mb-8">
                            {deliverables.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-white shadow-sm border border-gray-100 hover:border-emerald-300 hover:shadow-md transition-all">
                                    <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-800 font-medium text-lg">{item}</span>
                                </div>
                            ))}
                        </div>

                        <p className="text-emerald-800 font-medium italic mb-10 border-b border-gray-200 pb-6">
                            Every plant receives the right environment, the right nutrition, and the right care — at the right time.
                        </p>

                    </motion.div>

                    <div className="lg:w-1/2 relative">
                        <div className="sticky top-24 space-y-8">
                            <div className="grid grid-cols-2 gap-4 mb-8">
                                <img src={require('../../../assets/newimages/mas website protected farming.webp')} alt="Protected Farming" className="rounded-2xl w-full h-48 object-cover shadow-lg" />
                                <img src={require('../../../assets/newimages/mas wesbite protected farming (2).webp')} alt="Precision Tech" className="rounded-2xl w-full h-48 object-cover shadow-lg translate-y-6" />
                            </div>

                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100"
                            >
                                <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                                    Why It Matters
                                </h3>

                                <div className="space-y-6">
                                    {benefits.map((benefit, idx) => (
                                        <div key={idx} className="flex items-center gap-4">
                                            <div className="bg-emerald-100 p-3 rounded-xl">
                                                <benefit.icon className="w-6 h-6 text-emerald-600" />
                                            </div>
                                            <p className="text-gray-800 font-medium text-lg">{benefit.title}</p>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="bg-gray-900 p-10 rounded-3xl shadow-2xl text-white relative overflow-hidden"
                            >
                                <div className="absolute -right-20 -top-20 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl text-white"></div>

                                <h3 className="text-3xl font-bold mb-8 flex items-center gap-3 relative z-10 text-emerald-400">
                                    Farming with Control & Accuracy
                                </h3>

                                <div className="relative z-10 space-y-4 mb-8">
                                    <p className="text-xl font-light text-gray-300">
                                        Protected farming <span className="text-white font-medium">reduces</span> environmental risk.
                                    </p>
                                    <p className="text-xl font-light text-gray-300">
                                        Precision farming <span className="text-white font-medium">increases</span> operational accuracy.
                                    </p>
                                </div>

                                <p className="text-lg text-gray-400 font-light mb-10 relative z-10 border-b border-gray-800 pb-8">
                                    Together, they create a high-efficiency agricultural system built for sustainable growth and long-term profitability.
                                </p>

                                <div className="relative z-10">
                                    <div className="flex flex-col gap-3">
                                        <p className="text-xl font-bold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-300">Controlled Conditions.</p>
                                        <p className="text-xl font-bold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-300">Measured Inputs.</p>
                                        <p className="text-xl font-bold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-300">Maximized Output.</p>
                                    </div>
                                </div>
                            </motion.div>

                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PrecisionFarming;
